/*
 * THESIS: El bazar navideño boricua de noche, traducido del Toolkit Navimarket
 * 2026 a la web; rechaza la landing corporativa clara con secciones blancas.
 * OWN-WORLD: Verde pino drenched (#00322A) con glow #006050, acentos tangerine
 * /ámbar, crema para texto, Ubuntu, elementos 3D del toolkit flotando, luces
 * de guirnalda ámbar; radius generoso, botones píldora.
 * STORY: Un dueño de negocio entiende en segundos qué es Navimarket, que
 * participar es gratis, y envía su solicitud; un visitante encuentra fecha,
 * lugar y mapa.
 * FIRST VIEWPORT: Izquierda: fecha, lockup Navimarket, titular uppercase con
 * palabras en ámbar/naranja, CTA píldora naranja y stats; derecha: bodegón 3D
 * (logo ATH con luces + coquito, pandereta, pastel, gift, pascua) con parallax.
 * FORM: Composición del mockup aprobado del cliente — dirección pinneada por
 * brief (toolkit + mockup), sin tournament de conceptos.
 */
import {useEffect, useState} from 'react';
import {useReducedMotion} from 'motion/react';
import {Toaster} from '@/components/ui/sonner';
import {SiteHeader, type NavTarget} from '@/components/SiteHeader';
import {Hero} from '@/components/Hero';
import {AudienceTabs, type Audience} from '@/components/AudienceTabs';
import {SiteFooter} from '@/components/SiteFooter';
import {OfficialTermsProvider} from '@/components/vendors/OfficialTermsDialog';

/** A qué tab pertenece cada ancla de navegación. */
const TARGET_AUDIENCE: Record<NavTarget, Audience> = {
  evento: 'publico',
  recap: 'publico',
  vendors: 'vendors',
  registro: 'vendors',
};

/** Anclas que abren el panel de visitantes. "mapa" sigue aquí por los enlaces
 *  ya compartidos: el mapa está oculto, pero #mapa no debe caer en vendors. */
const PUBLIC_HASHES = ['publico', 'evento', 'recap', 'mapa'];

function initialAudience(): Audience {
  const h = window.location.hash.replace('#', '');
  return PUBLIC_HASHES.includes(h) ? 'publico' : 'vendors';
}

/** Cuánto esperamos como máximo a que aparezca la sección antes de rendirnos. */
const ANCHOR_TIMEOUT = 2000;

/**
 * Ancla a una sección en cuanto exista en el DOM.
 *
 * Cambiar de tab desmonta un panel y monta el otro, y el swap pasa por
 * AnimatePresence en modo "wait": el panel entrante no existe hasta que el
 * saliente termina de animar. Por eso no basta con esperar un par de frames —
 * hay que buscar la sección frame a frame hasta que aparezca.
 *
 * Devuelve una función para cancelar la espera.
 */
function scrollToWhenReady(id: string, smooth: boolean) {
  const deadline = performance.now() + ANCHOR_TIMEOUT;
  let frame = 0;

  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({behavior: smooth ? 'smooth' : 'auto'});
      return;
    }
    if (performance.now() < deadline) frame = requestAnimationFrame(tick);
  };

  frame = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(frame);
  };
}

export default function App() {
  const [audience, setAudience] = useState<Audience>(initialAudience);
  const reduced = useReducedMotion();
  // El hash con el que se cargó la página, leído en render: para cuando corran
  // los efectos, los hijos ya pudieron haberlo reescrito.
  const [entryHash] = useState(() => window.location.hash.replace('#', ''));

  // Llegadas con ancla desde otra página (p. ej. /reglas): el navegador intenta
  // saltar antes de que exista la sección, así que anclamos al montar.
  useEffect(() => {
    if (!entryHash) return;
    return scrollToWhenReady(entryHash, false);
  }, [entryHash]);

  /** Cambia de tab si hace falta y ancla a la sección cuando el panel monte. */
  function navigate(target: NavTarget) {
    setAudience(TARGET_AUDIENCE[target]);
    scrollToWhenReady(target, !reduced);
  }

  return (
    <OfficialTermsProvider>
      <div className="bg-pine-scene min-h-screen">
        <SiteHeader onNavigate={navigate} />
        <main>
          <Hero
            onVendorCta={() => {
              navigate('registro');
            }}
            onExplore={() => {
              navigate('evento');
            }}
          />
          <AudienceTabs value={audience} onChange={setAudience} />
        </main>
        <SiteFooter />
        <Toaster position="bottom-center" richColors />
      </div>
    </OfficialTermsProvider>
  );
}
